import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import {
  ArrowLeft, Plus, Upload, FileText, Sparkles, Loader2, Trophy, Star,
  TrendingUp, Shield, Heart, Globe, Download, Trash2, Edit, Check,
  DollarSign, Award, Target, Building2, ChevronDown, AlertCircle, X, Share2, UserPlus, Users,
  Eye, Printer
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { ClienteAtencionWatcher } from "../contexts/AtencionPrioritariaContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DEPT_CONFIG = {
  salud_local: { label: "Salud Local", icon: Heart, color: "emerald" },
  salud_internacional: { label: "Salud Internacional", icon: Globe, color: "sky" },
  riesgos_generales: { label: "Riesgos Generales", icon: Shield, color: "amber" },
};

const ASEGURADORAS = [
  "Humano Seguros", "Mapfre BHD Seguros", "Seguros Universal",
  "La Colonial de Seguros", "BMI", "WorldWide", "BUPA"
];

const CHART_COLORS = ["#10b981", "#0ea5e9", "#f59e0b", "#8b5cf6", "#ec4899", "#f97316", "#06b6d4"];

const SUPPORTED_EXTENSIONS = ".pdf,.xlsx,.xls";

const ETIQUETA_COLORS = {
  "MEJOR OPCIÓN": "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  "MEJOR PRECIO": "bg-sky-500/20 text-sky-300 border-sky-500/30",
  "MÁS COBERTURAS": "bg-purple-500/20 text-purple-300 border-purple-500/30",
  "EQUILIBRADA": "bg-amber-500/20 text-amber-300 border-amber-500/30",
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
};

export default function CotizacionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cotizacion, setCotizacion] = useState(null);
  const [activeTab, setActiveTab] = useState("comparativo");
  
  // Dialogs
  const [showAddPropuesta, setShowAddPropuesta] = useState(false);
  const [showUploadPdf, setShowUploadPdf] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form
  const [propForm, setPropForm] = useState({
    aseguradora: "", plan_nombre: "", moneda: "DOP",
    prima_mensual: "", prima_anual: "", prima_trimestral: "", prima_semestral: "", deducible_general: "",
    coaseguro: "", tope_cobertura: "", red_medica: "", area_cobertura: "",
    periodo_espera: "", edad_limite: "", preexistencias: "",
    coberturas: [], beneficios_adicionales: [], exclusiones: [], notas: ""
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfAseguradora, setPdfAseguradora] = useState("");
  const [editingPropuesta, setEditingPropuesta] = useState(null);
  const [aseguradorasList, setAseguradorasList] = useState(ASEGURADORAS);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedCoberturas, setExpandedCoberturas] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [reportForm, setReportForm] = useState({
    cliente_nombre: "", cliente_empresa: "", cliente_rnc: "",
    cliente_cargo: "", cliente_email: "", cliente_telefono: "", nota_personalizada: ""
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogPlanes, setCatalogPlanes] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [addingFromCatalog, setAddingFromCatalog] = useState(null);
  
  // Coverage form
  const [newCob, setNewCob] = useState({ nombre: "", limite: "", deducible: "", copago: "" });
  const [newBeneficio, setNewBeneficio] = useState("");
  
  const fileInputRef = useRef(null);

  const fetchCotizacion = useCallback(async () => {
    try {
      setLoading(true);
      const [res, configRes] = await Promise.all([
        axios.get(`${API}/cotizaciones/${id}`, getAuthHeaders()),
        axios.get(`${API}/cotizaciones/admin-config`, getAuthHeaders()).catch(() => null),
      ]);
      setCotizacion(res.data);
      if (configRes?.data?.aseguradoras?.length) {
        setAseguradorasList(configRes.data.aseguradoras);
      }
    } catch (err) {
      toast.error("Error cargando cotización");
      navigate("/cotizaciones");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchCotizacion(); }, [fetchCotizacion]);

  const handleAnalyze = async () => {
    if ((cotizacion?.propuestas?.length || 0) < 2) {
      toast.error("Necesita al menos 2 propuestas para analizar"); return;
    }
    try {
      setAnalyzing(true);
      toast.info("Generando análisis con IA... Esto puede tardar unos segundos.");
      const res = await axios.post(`${API}/cotizaciones/${id}/analyze`, {}, {
        ...getAuthHeaders(),
        timeout: 120000,
      });
      toast.success("Análisis generado exitosamente");
      fetchCotizacion();
      setActiveTab("analisis");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        toast.error("El análisis tardó demasiado. Intente nuevamente.");
      } else {
        toast.error(detail || "Error al generar análisis. Intente nuevamente.");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUploadPdf = async () => {
    if (!pdfFile || !pdfAseguradora) {
      toast.error("Seleccione archivo y aseguradora"); return;
    }
    try {
      setUploading(true);
      toast.info("Extrayendo datos con IA... Esto puede tardar unos segundos.");
      const formData = new FormData();
      formData.append("file", pdfFile);
      formData.append("aseguradora", pdfAseguradora);
      const res = await axios.post(`${API}/cotizaciones/${id}/extract-document`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`Propuesta de ${pdfAseguradora} extraída exitosamente`);
      setShowUploadPdf(false);
      setPdfFile(null);
      setPdfAseguradora("");
      fetchCotizacion();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        toast.error("La extracción tardó demasiado. Intente con un documento más pequeño.");
      } else {
        toast.error(detail || "Error al procesar documento. Intente nuevamente.");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSavePropuesta = async () => {
    if (!propForm.aseguradora || !propForm.plan_nombre) {
      toast.error("Aseguradora y nombre del plan son requeridos"); return;
    }
    try {
      setSaving(true);
      const payload = {
        ...propForm,
        prima_mensual: propForm.prima_mensual ? parseFloat(propForm.prima_mensual) : null,
        prima_trimestral: propForm.prima_trimestral ? parseFloat(propForm.prima_trimestral) : null,
        prima_semestral: propForm.prima_semestral ? parseFloat(propForm.prima_semestral) : null,
        prima_anual: propForm.prima_anual ? parseFloat(propForm.prima_anual) : null,
      };
      
      if (editingPropuesta) {
        await axios.put(`${API}/cotizaciones/${id}/propuestas/${editingPropuesta}`, payload, getAuthHeaders());
        toast.success("Propuesta actualizada");
      } else {
        await axios.post(`${API}/cotizaciones/${id}/propuestas`, payload, getAuthHeaders());
        toast.success("Propuesta agregada");
      }
      setShowAddPropuesta(false);
      setEditingPropuesta(null);
      resetPropForm();
      fetchCotizacion();
    } catch (err) {
      toast.error("Error al guardar propuesta");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePropuesta = async (propId) => {
    if (!window.confirm("¿Eliminar esta propuesta?")) return;
    try {
      await axios.delete(`${API}/cotizaciones/${id}/propuestas/${propId}`, getAuthHeaders());
      toast.success("Propuesta eliminada");
      fetchCotizacion();
    } catch (err) {
      toast.error("Error al eliminar propuesta");
    }
  };

  const fetchCatalogPlanes = async () => {
    try {
      const res = await axios.get(`${API}/cotizaciones/${id}/catalog-plans`, getAuthHeaders());
      setCatalogPlanes(res.data?.planes || []);
    } catch { setCatalogPlanes([]); }
  };

  const handleAddFromCatalog = async (plan) => {
    try {
      setAddingFromCatalog(plan.nombre);
      await axios.post(`${API}/cotizaciones/${id}/propuestas-from-catalog`, {
        plan_nombre: plan.nombre,
        aseguradora: plan.aseguradora,
      }, getAuthHeaders());
      toast.success(`Plan "${plan.nombre}" agregado desde catálogo`);
      fetchCotizacion();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar plan");
    } finally {
      setAddingFromCatalog(null);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      // Revoke previous URL to avoid memory leaks
      if (previewUrl) {
        try { window.URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      }
      const res = await axios.get(`${API}/cotizaciones/${id}/preview-html`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreviewModal(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al cargar la vista previa";
      toast.error(detail);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    if (previewUrl) {
      try { window.URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      setPreviewUrl("");
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/cotizaciones/${id}/export-excel`, {
        ...getAuthHeaders(),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `comparativo_${cotizacion?.titulo?.replace(/\s/g, "_") || "cotizacion"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel descargado");
    } catch (err) {
      toast.error("Error al exportar Excel");
    }
  };

  const handleExportReport = async (formato) => {
    try {
      setExportingReport(true);
      const payload = { ...reportForm, formato };
      const res = await axios.post(`${API}/cotizaciones/${id}/export-report`, payload, {
        ...getAuthHeaders(),
        responseType: "blob",
        timeout: 60000,
      });
      const ext = formato === "pdf" ? "pdf" : "docx";
      const mime = formato === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `informe_${cotizacion?.titulo?.replace(/\s/g, "_") || "cotizacion"}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Informe ${ext.toUpperCase()} descargado`);
    } catch (err) {
      // Si el response es blob, axios no parsea automáticamente el JSON de error.
      // Lo extraemos manualmente para mostrar el mensaje real del backend.
      let detail = "Error al generar el informe";
      const errData = err?.response?.data;
      if (errData instanceof Blob) {
        try {
          const text = await errData.text();
          try {
            const parsed = JSON.parse(text);
            detail = parsed.detail || parsed.message || text.slice(0, 300);
          } catch {
            detail = text.slice(0, 300) || detail;
          }
        } catch {
          // ignore
        }
      } else if (typeof errData === "object" && errData?.detail) {
        detail = errData.detail;
      }
      // Log completo a consola para debug
      console.error("[export-report] error:", err?.response?.status, detail, err);
      toast.error(detail);
    } finally {
      setExportingReport(false);
    }
  };

  const handleOpenShare = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`, getAuthHeaders());
      const users = (res.data || []).filter(u => u.id !== cotizacion?.created_by);
      setPlatformUsers(users);
      const alreadyShared = (cotizacion?.compartida_con || []).map(s => s.user_id);
      setSelectedShareUsers(alreadyShared);
      setShowShareModal(true);
    } catch { setPlatformUsers([]); setShowShareModal(true); }
  };

  const handleShare = async () => {
    if (!selectedShareUsers.length) { toast.error("Seleccione al menos un usuario"); return; }
    try {
      setSharing(true);
      await axios.post(`${API}/cotizaciones/${id}/share`, { user_ids: selectedShareUsers }, getAuthHeaders());
      toast.success("Cotización compartida exitosamente");
      setShowShareModal(false);
      fetchCotizacion();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al compartir");
    } finally { setSharing(false); }
  };

  const toggleShareUser = (userId) => {
    setSelectedShareUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };


  const resetPropForm = () => {
    setPropForm({
      aseguradora: "", plan_nombre: "", moneda: "DOP",
      prima_mensual: "", prima_anual: "", prima_trimestral: "", prima_semestral: "", deducible_general: "",
      coaseguro: "", tope_cobertura: "", red_medica: "", area_cobertura: "",
      periodo_espera: "", edad_limite: "", preexistencias: "",
      coberturas: [], beneficios_adicionales: [], exclusiones: [], notas: ""
    });
  };

  const openEditPropuesta = (p) => {
    setPropForm({
      aseguradora: p.aseguradora || "",
      plan_nombre: p.plan_nombre || "",
      moneda: p.moneda || "DOP",
      prima_mensual: p.prima_mensual?.toString() || "",
      prima_trimestral: p.prima_trimestral?.toString() || "",
      prima_semestral: p.prima_semestral?.toString() || "",
      prima_anual: p.prima_anual?.toString() || "",
      deducible_general: p.deducible_general || "",
      coaseguro: p.coaseguro || "",
      tope_cobertura: p.tope_cobertura || "",
      red_medica: p.red_medica || "",
      area_cobertura: p.area_cobertura || "",
      periodo_espera: p.periodo_espera || "",
      edad_limite: p.edad_limite || "",
      preexistencias: p.preexistencias || "",
      coberturas: p.coberturas || [],
      beneficios_adicionales: p.beneficios_adicionales || [],
      exclusiones: p.exclusiones || [],
      notas: p.notas || ""
    });
    setEditingPropuesta(p.id);
    setShowAddPropuesta(true);
  };

  const addCobertura = () => {
    if (!newCob.nombre) return;
    setPropForm({ ...propForm, coberturas: [...propForm.coberturas, { ...newCob }] });
    setNewCob({ nombre: "", limite: "", deducible: "", copago: "" });
  };

  const removeCobertura = (idx) => {
    setPropForm({ ...propForm, coberturas: propForm.coberturas.filter((_, i) => i !== idx) });
  };

  const addBeneficio = () => {
    if (!newBeneficio.trim()) return;
    setPropForm({ ...propForm, beneficios_adicionales: [...propForm.beneficios_adicionales, newBeneficio.trim()] });
    setNewBeneficio("");
  };

  if (loading || !cotizacion) {
    return (
      <div className="flex min-h-screen bg-slate-950">
        <Sidebar />
        <div className="flex-1 ml-0 md:ml-64 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
        </div>
      </div>
    );
  }

  const propuestas = cotizacion.propuestas || [];
  const analisis = cotizacion.analisis_ai;
  const deptCfg = DEPT_CONFIG[cotizacion.departamento] || {};

  // Chart data
  const primasChartData = propuestas.map((p, i) => ({
    name: p.aseguradora?.split(" ").slice(0, 2).join(" ") || `P${i + 1}`,
    mensual: p.prima_mensual || 0,
    trimestral: p.prima_trimestral || 0,
    semestral: p.prima_semestral || 0,
    anual: p.prima_anual || 0,
    fill: CHART_COLORS[i % CHART_COLORS.length]
  })).sort((a, b) => a.anual - b.anual);

  const radarData = analisis?.ranking ? (() => {
    const categories = ["Precio", "Coberturas", "Beneficios", "Red Médica", "Deducibles"];
    const keys = ["puntaje_precio", "puntaje_coberturas", "puntaje_beneficios", "puntaje_red_medica", "puntaje_deducibles"];
    return categories.map((cat, i) => {
      const d = { subject: cat };
      analisis.ranking.slice(0, 4).forEach(r => {
        d[r.aseguradora?.split(" ").slice(0, 2).join(" ")] = r[keys[i]] || 0;
      });
      return d;
    });
  })() : [];

  // Coverage comparison: group proposals by aseguradora for spanning headers
  const asegGroups = [];
  const asegMap = {};
  propuestas.forEach(p => {
    if (!asegMap[p.aseguradora]) {
      asegMap[p.aseguradora] = { name: p.aseguradora, plans: [] };
      asegGroups.push(asegMap[p.aseguradora]);
    }
    asegMap[p.aseguradora].plans.push(p);
  });

  return (
    <div className="flex min-h-screen bg-slate-950">
      <Sidebar />
      <ClienteAtencionWatcher nombre={cotizacion?.cliente_nombre} />
      <div className="flex-1 ml-0 md:ml-64">
        <main className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="cotizacion-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/cotizaciones")} className="text-slate-400 hover:text-slate-200">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-50 tracking-tight">{cotizacion.titulo}</h1>
              <Badge className="bg-slate-800 text-slate-300 rounded-lg">{deptCfg.label}</Badge>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              {cotizacion.cliente_nombre} {cotizacion.ramo ? `| ${cotizacion.ramo}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowUploadPdf(true); }}
              className="border-sky-800 text-sky-300 hover:bg-sky-900/30 rounded-xl gap-2"
              data-testid="upload-pdf-btn"
            >
              <Upload className="w-4 h-4" /> Cargar Documento
            </Button>
            <Button
              variant="outline"
              onClick={() => { resetPropForm(); setEditingPropuesta(null); setShowAddPropuesta(true); }}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl gap-2"
              data-testid="add-propuesta-btn"
            >
              <Plus className="w-4 h-4" /> Agregar Manual
            </Button>
            <Button
              variant="outline"
              onClick={() => { fetchCatalogPlanes(); setShowCatalogModal(true); setCatalogSearch(""); }}
              className="border-teal-800 text-teal-300 hover:bg-teal-900/30 rounded-xl gap-2"
              data-testid="add-from-catalog-btn"
            >
              <Building2 className="w-4 h-4" /> Desde Catálogo
            </Button>
            {propuestas.length >= 2 && (
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="bg-gradient-to-r from-sky-600 to-emerald-600 hover:from-sky-500 hover:to-emerald-500 text-white rounded-xl gap-2"
                data-testid="analyze-btn"
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Analizar con IA
              </Button>
            )}
            {propuestas.length > 0 && (
              <Button
                variant="outline"
                onClick={handlePreview}
                disabled={previewLoading}
                className="border-sky-700 text-sky-300 hover:bg-sky-900/30 rounded-xl gap-2"
                data-testid="preview-cotizacion-btn"
              >
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Vista Previa
              </Button>
            )}
            {propuestas.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExportExcel}
                className="border-emerald-800 text-emerald-300 hover:bg-emerald-900/30 rounded-xl gap-2"
                data-testid="export-excel-btn"
              >
                <Download className="w-4 h-4" /> Excel
              </Button>
            )}
            {analisis && (
              <Button
                onClick={() => {
                  setReportForm({
                    cliente_nombre: cotizacion.cliente_nombre || "",
                    cliente_empresa: cotizacion.cliente_nombre || "",
                    cliente_rnc: cotizacion.cliente_rnc || "",
                    cliente_cargo: "",
                    cliente_email: cotizacion.cliente_email || "",
                    cliente_telefono: cotizacion.cliente_telefono || "",
                    nota_personalizada: "",
                  });
                  setShowReportModal(true);
                }}
                className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white rounded-xl gap-2"
                data-testid="personalize-report-btn"
              >
                <FileText className="w-4 h-4" /> Personalizar Cotización
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleOpenShare}
              className="border-violet-800 text-violet-300 hover:bg-violet-900/30 rounded-xl gap-2"
              data-testid="share-cotizacion-btn"
            >
              <Share2 className="w-4 h-4" /> Compartir
            </Button>
          </div>
        </div>

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800 rounded-xl mb-6">
            <TabsTrigger value="comparativo" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg">
              Comparativo
            </TabsTrigger>
            <TabsTrigger value="analisis" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white rounded-lg" disabled={!analisis}>
              Análisis IA
            </TabsTrigger>
            <TabsTrigger value="graficos" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg" disabled={propuestas.length === 0}>
              Gráficos
            </TabsTrigger>
          </TabsList>

          {/* === TAB: COMPARATIVO === */}
          <TabsContent value="comparativo">
            {propuestas.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800 rounded-2xl">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-300 mb-2">Sin propuestas</h3>
                  <p className="text-slate-500 text-sm mb-6">Agregue propuestas de aseguradoras para comenzar el comparativo</p>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowUploadPdf(true)} className="bg-sky-600 hover:bg-sky-500 rounded-xl gap-2">
                      <Upload className="w-4 h-4" /> Cargar Documento
                    </Button>
                    <Button variant="outline" onClick={() => { resetPropForm(); setShowAddPropuesta(true); }} className="border-slate-700 text-slate-300 rounded-xl gap-2">
                      <Plus className="w-4 h-4" /> Manual
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Propuestas sorted by aseguradora ascending */}
                {[...propuestas].sort((a, b) => (a.aseguradora || "").localeCompare(b.aseguradora || "")).map((p, idx) => {
                  const rankItem = analisis?.ranking?.find(r => r.aseguradora === p.aseguradora);
                  return (
                    <Card key={p.id} className="bg-slate-900 border-slate-800 rounded-2xl hover:border-slate-700 transition-all" data-testid={`propuesta-card-${p.id}`}>
                      <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <CardTitle className="text-lg text-slate-100">{p.aseguradora}</CardTitle>
                            <p className="text-sm text-slate-400">{p.plan_nombre}</p>
                          </div>
                          {rankItem?.etiqueta && (
                            <Badge className={`${ETIQUETA_COLORS[rankItem.etiqueta] || "bg-slate-700 text-slate-300"} border rounded-lg text-xs px-2`}>
                              {rankItem.posicion === 1 && <Trophy className="w-3 h-3 mr-1" />}
                              {rankItem.etiqueta}
                            </Badge>
                          )}
                          {p.fuente === "pdf" && (
                            <Badge className="bg-sky-900/50 text-sky-300 text-xs rounded-lg">PDF</Badge>
                          )}
                          {(p.fuente === "xlsx" || p.fuente === "xls") && (
                            <Badge className="bg-emerald-900/50 text-emerald-300 text-xs rounded-lg">Excel</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {rankItem && (
                            <div className="text-right mr-4">
                              <p className="text-xs text-slate-500">Puntaje Global</p>
                              <p className={`text-xl font-bold ${rankItem.puntaje_global >= 80 ? "text-emerald-400" : rankItem.puntaje_global >= 60 ? "text-amber-400" : "text-red-400"}`}>
                                {rankItem.puntaje_global}
                              </p>
                            </div>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => openEditPropuesta(p)} className="text-slate-500 hover:text-sky-400">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeletePropuesta(p.id)} className="text-slate-500 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {/* Key metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                          {p.prima_mensual && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Prima Mensual</p>
                              <p className="font-semibold text-slate-200">{p.moneda === "USD" ? "US$" : "RD$"} {p.prima_mensual?.toLocaleString("es-DO")}</p>
                            </div>
                          )}
                          {p.prima_trimestral && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Prima Trimestral</p>
                              <p className="font-semibold text-slate-200">{p.moneda === "USD" ? "US$" : "RD$"} {p.prima_trimestral?.toLocaleString("es-DO")}</p>
                            </div>
                          )}
                          {p.prima_semestral && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Prima Semestral</p>
                              <p className="font-semibold text-slate-200">{p.moneda === "USD" ? "US$" : "RD$"} {p.prima_semestral?.toLocaleString("es-DO")}</p>
                            </div>
                          )}
                          {p.prima_anual && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Prima Anual</p>
                              <p className="font-semibold text-slate-200">{p.moneda === "USD" ? "US$" : "RD$"} {p.prima_anual?.toLocaleString("es-DO")}</p>
                            </div>
                          )}
                          {p.deducible_general && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Deducible</p>
                              <p className="font-semibold text-slate-200 text-sm">{p.deducible_general}</p>
                            </div>
                          )}
                          {p.coaseguro && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Coaseguro</p>
                              <p className="font-semibold text-slate-200 text-sm">{p.coaseguro}</p>
                            </div>
                          )}
                          {p.tope_cobertura && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Tope Cobertura</p>
                              <p className="font-semibold text-slate-200 text-sm">{p.tope_cobertura}</p>
                            </div>
                          )}
                          {p.red_medica && (
                            <div className="bg-slate-800/50 p-3 rounded-xl">
                              <p className="text-xs text-slate-500">Red Médica</p>
                              <p className="font-semibold text-slate-200 text-sm">{p.red_medica}</p>
                            </div>
                          )}
                        </div>

                        {/* Coberturas */}
                        {p.coberturas?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Coberturas ({p.coberturas.length})</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {(expandedCoberturas[p.id] ? p.coberturas : p.coberturas.slice(0, 6)).map((c, ci) => (
                                <div key={ci} className="bg-slate-800/30 border border-slate-800 rounded-lg p-2">
                                  <p className="text-sm text-slate-200 font-medium">{c.nombre}</p>
                                  <div className="flex gap-3 text-xs text-slate-500 mt-1">
                                    {c.limite && <span>Lím: {c.limite}</span>}
                                    {c.deducible && <span>Ded: {c.deducible}</span>}
                                    {c.copago && <span>Cop: {c.copago}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {p.coberturas.length > 6 && (
                              <button
                                onClick={() => setExpandedCoberturas(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                                className="mt-2 w-full py-1.5 text-xs text-sky-400 hover:text-sky-300 bg-slate-800/40 hover:bg-slate-800/60 border border-slate-800 rounded-lg transition-colors flex items-center justify-center gap-1"
                                data-testid={`toggle-coberturas-${p.id}`}
                              >
                                {expandedCoberturas[p.id]
                                  ? "Ocultar coberturas"
                                  : `Ver las ${p.coberturas.length - 6} coberturas restantes`}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Benefits */}
                        {p.beneficios_adicionales?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Beneficios</p>
                            <div className="flex flex-wrap gap-1.5">
                              {p.beneficios_adicionales.map((b, bi) => (
                                <Badge key={bi} variant="outline" className="border-emerald-800 text-emerald-300 text-xs rounded-lg">
                                  <Check className="w-3 h-3 mr-1" />{b}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* === TAB: ANÁLISIS IA === */}
          <TabsContent value="analisis">
            {analisis ? (
              <div className="space-y-6">
                {/* AI Recommendation Hero */}
                <Card className="relative overflow-hidden border-sky-900/50 shadow-[0_0_40px_-15px_rgba(14,165,233,0.3)] rounded-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-sky-950/50 to-emerald-950/40" />
                  <CardContent className="relative z-10 p-8">
                    <div className="flex items-start gap-2 mb-4">
                      <Sparkles className="w-6 h-6 text-sky-400" />
                      <h2 className="text-2xl font-bold text-slate-50 tracking-tight">Dictamen IA</h2>
                    </div>
                    {analisis.resumen_ejecutivo && (
                      <p className="text-lg text-slate-200 leading-relaxed mb-6 max-w-4xl">
                        {analisis.resumen_ejecutivo}
                      </p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {analisis.recomendacion?.mejor_opcion_global && (
                        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Trophy className="w-5 h-5 text-emerald-400" />
                            <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Mejor Opción Global</p>
                          </div>
                          <p className="text-lg font-bold text-slate-100">{analisis.recomendacion.mejor_opcion_global}</p>
                        </div>
                      )}
                      {analisis.recomendacion?.mejor_precio && (
                        <div className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-5 h-5 text-sky-400" />
                            <p className="text-xs font-semibold text-sky-400 uppercase tracking-wider">Mejor Precio</p>
                          </div>
                          <p className="text-lg font-bold text-slate-100">{analisis.recomendacion.mejor_precio}</p>
                        </div>
                      )}
                      {analisis.recomendacion?.mejores_coberturas && (
                        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-5 h-5 text-purple-400" />
                            <p className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Mejores Coberturas</p>
                          </div>
                          <p className="text-lg font-bold text-slate-100">{analisis.recomendacion.mejores_coberturas}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Explanation */}
                {analisis.recomendacion?.explicacion_detallada && (
                  <Card className="bg-slate-900 border-slate-800 rounded-2xl">
                    <CardHeader>
                      <CardTitle className="text-slate-100 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-sky-400" /> Análisis Detallado
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-300 leading-relaxed whitespace-pre-line">
                        {analisis.recomendacion.explicacion_detallada}
                      </p>
                      {analisis.recomendacion.consideraciones?.length > 0 && (
                        <div className="mt-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-3">Consideraciones</p>
                          <ul className="space-y-2">
                            {analisis.recomendacion.consideraciones.map((c, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Ranking */}
                {analisis.ranking?.length > 0 && (
                  <Card className="bg-slate-900 border-slate-800 rounded-2xl">
                    <CardHeader>
                      <CardTitle className="text-slate-100 flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-400" /> Ranking de Aseguradoras
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analisis.ranking.map((r, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center gap-4 p-4 rounded-xl border ${idx === 0 ? "bg-emerald-500/5 border-emerald-500/20" : "bg-slate-800/50 border-slate-800"}`}
                            data-testid={`ranking-item-${idx}`}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${idx === 0 ? "bg-emerald-600 text-white" : idx === 1 ? "bg-slate-600 text-white" : "bg-slate-700 text-slate-300"}`}>
                              {r.posicion}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-100">{r.aseguradora}</p>
                                {r.etiqueta && (
                                  <Badge className={`${ETIQUETA_COLORS[r.etiqueta] || "bg-slate-700 text-slate-300"} border rounded-lg text-xs`}>
                                    {r.etiqueta}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-400">{r.plan}</p>
                              {r.fortalezas?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {r.fortalezas.map((f, fi) => (
                                    <span key={fi} className="text-xs text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded">{f}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-5 gap-3 text-center">
                              {[
                                { label: "Global", value: r.puntaje_global },
                                { label: "Precio", value: r.puntaje_precio },
                                { label: "Cobert.", value: r.puntaje_coberturas },
                                { label: "Benef.", value: r.puntaje_beneficios },
                                { label: "Deduc.", value: r.puntaje_deducibles },
                              ].map((s, si) => (
                                <div key={si}>
                                  <p className="text-[10px] text-slate-500">{s.label}</p>
                                  <p className={`text-sm font-bold ${s.value >= 80 ? "text-emerald-400" : s.value >= 60 ? "text-amber-400" : "text-red-400"}`}>
                                    {s.value}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card className="bg-slate-900 border-slate-800 rounded-2xl">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Sparkles className="w-12 h-12 text-slate-600 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-300 mb-2">Sin análisis generado</h3>
                  <p className="text-slate-500 text-sm mb-4">Agregue al menos 2 propuestas y genere el análisis con IA</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* === TAB: GRÁFICOS === */}
          <TabsContent value="graficos">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar Chart - Primas */}
              <Card className="bg-slate-900 border-slate-800 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-slate-100 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" /> Comparativo de Primas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {primasChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={primasChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "12px", color: "#e2e8f0" }}
                          formatter={(v) => [`${v?.toLocaleString("es-DO")}`, ""]}
                        />
                        <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 11 }} />
                        <Bar dataKey="mensual" name="Mensual" radius={[8, 8, 0, 0]} fill="#60a5fa" />
                        <Bar dataKey="trimestral" name="Trimestral" radius={[8, 8, 0, 0]} fill="#34d399" />
                        <Bar dataKey="semestral" name="Semestral" radius={[8, 8, 0, 0]} fill="#fbbf24" />
                        <Bar dataKey="anual" name="Anual" radius={[8, 8, 0, 0]} fill="#f472b6" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-slate-500 text-center py-8">Sin datos de primas</p>
                  )}
                </CardContent>
              </Card>

              {/* Radar Chart - Scoring */}
              <Card className="bg-slate-900 border-slate-800 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-slate-100 flex items-center gap-2">
                    <Target className="w-5 h-5 text-sky-400" /> Evaluación por Categoría
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {radarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#334155" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 9 }} />
                        {analisis.ranking.slice(0, 4).map((r, i) => (
                          <Radar
                            key={r.aseguradora}
                            name={r.aseguradora?.split(" ").slice(0, 2).join(" ")}
                            dataKey={r.aseguradora?.split(" ").slice(0, 2).join(" ")}
                            stroke={CHART_COLORS[i]}
                            fill={CHART_COLORS[i]}
                            fillOpacity={0.15}
                            strokeWidth={2}
                          />
                        ))}
                        <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 11 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-slate-500 text-center py-8">Genere el análisis IA para ver la evaluación</p>
                  )}
                </CardContent>
              </Card>

              {/* Coverage Comparison */}
              {analisis?.comparativa_coberturas?.length > 0 && (
                <Card className="bg-slate-900 border-slate-800 rounded-2xl lg:col-span-2" data-testid="coverage-comparison-card">
                  <CardHeader>
                    <CardTitle className="text-slate-100 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-400" /> Comparativa de Coberturas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          {/* Row 1: Aseguradora names spanning their plans */}
                          <tr className="border-b border-slate-700">
                            <th rowSpan={2} className="text-left py-3 px-4 text-slate-400 font-semibold align-bottom border-r border-slate-800">Cobertura</th>
                            {asegGroups.map(g => (
                              <th key={g.name} colSpan={g.plans.length} className="text-center py-2 px-4 font-bold bg-slate-800/80 border-x border-slate-700" style={{ color: '#8bb8f0' }}>
                                {g.name}
                              </th>
                            ))}
                          </tr>
                          {/* Row 2: Plan names */}
                          <tr className="border-b border-slate-800 bg-slate-800/40">
                            {propuestas.map(p => (
                              <th key={p.id} className="text-center py-2 px-3 text-slate-300 font-medium text-xs border-x border-slate-800/50">
                                {p.plan_nombre || "Plan"}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {analisis.comparativa_coberturas.map((cob, i) => (
                            <tr key={i} className={`border-b border-slate-800/50 ${cob.es_critica ? "bg-amber-500/5" : ""}`}>
                              <td className="py-3 px-4 text-slate-200 font-medium border-r border-slate-800">
                                {cob.cobertura}
                                {cob.es_critica && <Badge className="ml-2 bg-amber-900/30 text-amber-400 text-[10px]">Crítica</Badge>}
                              </td>
                              {propuestas.map(p => {
                                const compositeKey = `${p.aseguradora} | ${p.plan_nombre}`;
                                const val = cob.valores?.[compositeKey] || cob.valores?.[p.aseguradora] || "—";
                                return (
                                  <td key={p.id} className="py-3 px-3 text-center text-slate-300 text-xs border-x border-slate-800/30">
                                    {val}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* === DIALOGS === */}

        {/* Upload Document Dialog */}
        <Dialog open={showUploadPdf} onOpenChange={setShowUploadPdf}>
          <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-slate-50 flex items-center gap-2">
                <Upload className="w-5 h-5 text-sky-400" /> Cargar Documento
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Suba un PDF o Excel (.xlsx) y la IA extraerá los datos automáticamente
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Aseguradora *</Label>
                <Select value={pdfAseguradora} onValueChange={setPdfAseguradora}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100" data-testid="pdf-aseguradora-select">
                    <SelectValue placeholder="Seleccione aseguradora" />
                  </SelectTrigger>
                  <SelectContent>
                    {aseguradorasList.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Archivo (PDF o Excel) *</Label>
                <div
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
                    isDragging
                      ? "border-sky-400 bg-sky-900/20 scale-[1.02]"
                      : "border-slate-700 hover:border-sky-600"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    const droppedFile = e.dataTransfer.files?.[0];
                    if (droppedFile) {
                      const ext = droppedFile.name.split(".").pop()?.toLowerCase();
                      if (["pdf", "xlsx", "xls"].includes(ext)) {
                        setPdfFile(droppedFile);
                      } else {
                        toast.error("Solo se aceptan archivos PDF o Excel (.xlsx, .xls)");
                      }
                    }
                  }}
                  data-testid="file-drop-zone"
                >
                  {pdfFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-5 h-5 text-sky-400" />
                      <span className="text-slate-200">{pdfFile.name}</span>
                      <span className="text-xs text-slate-500">({(pdfFile.size / 1024).toFixed(0)} KB)</span>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setPdfFile(null); }} className="w-6 h-6">
                        <X className="w-4 h-4 text-slate-500" />
                      </Button>
                    </div>
                  ) : isDragging ? (
                    <>
                      <Upload className="w-8 h-8 text-sky-400 mx-auto mb-2 animate-bounce" />
                      <p className="text-sm text-sky-300 font-medium">Suelte el archivo aquí</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Arrastre un archivo aquí o haga clic para seleccionar</p>
                      <p className="text-xs text-slate-500 mt-1">.pdf, .xlsx, .xls</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={SUPPORTED_EXTENSIONS}
                  className="hidden"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  data-testid="file-upload-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadPdf(false)} className="border-slate-700 text-slate-300">Cancelar</Button>
              <Button
                onClick={handleUploadPdf}
                disabled={uploading || !pdfFile || !pdfAseguradora}
                className="bg-sky-600 hover:bg-sky-500 rounded-xl gap-2"
                data-testid="upload-pdf-submit"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Extraer con IA
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Propuesta Dialog */}
        <Dialog open={showAddPropuesta} onOpenChange={(v) => { setShowAddPropuesta(v); if (!v) setEditingPropuesta(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-slate-50 flex items-center gap-2">
                {editingPropuesta ? <Edit className="w-5 h-5 text-sky-400" /> : <Plus className="w-5 h-5 text-emerald-400" />}
                {editingPropuesta ? "Editar Propuesta" : "Agregar Propuesta Manual"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Aseguradora *</Label>
                  <Select value={propForm.aseguradora} onValueChange={(v) => setPropForm({...propForm, aseguradora: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100" data-testid="manual-aseguradora-select">
                      <SelectValue placeholder="Seleccione" />
                    </SelectTrigger>
                    <SelectContent>
                      {aseguradorasList.map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Nombre del Plan *</Label>
                  <Input value={propForm.plan_nombre} onChange={(e) => setPropForm({...propForm, plan_nombre: e.target.value})} placeholder="Ej: Plan Oro" className="bg-slate-800 border-slate-700 text-slate-100" data-testid="manual-plan-nombre" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-300">Moneda</Label>
                  <Select value={propForm.moneda} onValueChange={(v) => setPropForm({...propForm, moneda: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">DOP (RD$)</SelectItem>
                      <SelectItem value="USD">USD (US$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Prima Mensual</Label>
                  <Input type="number" value={propForm.prima_mensual} onChange={(e) => setPropForm({...propForm, prima_mensual: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Prima Trimestral</Label>
                  <Input type="number" value={propForm.prima_trimestral || ""} onChange={(e) => setPropForm({...propForm, prima_trimestral: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Prima Semestral</Label>
                  <Input type="number" value={propForm.prima_semestral || ""} onChange={(e) => setPropForm({...propForm, prima_semestral: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Prima Anual</Label>
                  <Input type="number" value={propForm.prima_anual} onChange={(e) => setPropForm({...propForm, prima_anual: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Deducible General</Label>
                  <Input value={propForm.deducible_general} onChange={(e) => setPropForm({...propForm, deducible_general: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Coaseguro</Label>
                  <Input value={propForm.coaseguro} onChange={(e) => setPropForm({...propForm, coaseguro: e.target.value})} placeholder="Ej: 80/20" className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Tope de Cobertura</Label>
                  <Input value={propForm.tope_cobertura} onChange={(e) => setPropForm({...propForm, tope_cobertura: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Red Médica</Label>
                  <Input value={propForm.red_medica} onChange={(e) => setPropForm({...propForm, red_medica: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" />
                </div>
              </div>

              {/* Coberturas */}
              <div className="border border-slate-800 rounded-xl p-4">
                <p className="text-sm font-semibold text-slate-300 mb-3">Coberturas</p>
                {propForm.coberturas.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {propForm.coberturas.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-2">
                        <span className="text-sm text-slate-200 flex-1">{c.nombre}</span>
                        <span className="text-xs text-slate-500">{c.limite}</span>
                        <Button variant="ghost" size="icon" className="w-6 h-6" onClick={() => removeCobertura(i)}>
                          <X className="w-3 h-3 text-slate-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-5 gap-2">
                  <Input placeholder="Nombre" value={newCob.nombre} onChange={(e) => setNewCob({...newCob, nombre: e.target.value})} className="col-span-2 bg-slate-800 border-slate-700 text-slate-100 text-xs" />
                  <Input placeholder="Límite" value={newCob.limite} onChange={(e) => setNewCob({...newCob, limite: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100 text-xs" />
                  <Input placeholder="Deducible" value={newCob.deducible} onChange={(e) => setNewCob({...newCob, deducible: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100 text-xs" />
                  <Button variant="outline" size="sm" onClick={addCobertura} className="border-slate-700 text-slate-300">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Benefits */}
              <div className="border border-slate-800 rounded-xl p-4">
                <p className="text-sm font-semibold text-slate-300 mb-3">Beneficios Adicionales</p>
                {propForm.beneficios_adicionales.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {propForm.beneficios_adicionales.map((b, i) => (
                      <Badge key={i} variant="outline" className="border-emerald-800 text-emerald-300 gap-1">
                        {b}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => setPropForm({...propForm, beneficios_adicionales: propForm.beneficios_adicionales.filter((_, j) => j !== i)})} />
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input placeholder="Nuevo beneficio" value={newBeneficio} onChange={(e) => setNewBeneficio(e.target.value)} className="bg-slate-800 border-slate-700 text-slate-100 text-sm" onKeyDown={(e) => e.key === "Enter" && addBeneficio()} />
                  <Button variant="outline" size="sm" onClick={addBeneficio} className="border-slate-700 text-slate-300">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Notas</Label>
                <Textarea value={propForm.notas} onChange={(e) => setPropForm({...propForm, notas: e.target.value})} className="bg-slate-800 border-slate-700 text-slate-100" rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddPropuesta(false); setEditingPropuesta(null); }} className="border-slate-700 text-slate-300">Cancelar</Button>
              <Button
                onClick={handleSavePropuesta}
                disabled={saving || !propForm.aseguradora || !propForm.plan_nombre}
                className="bg-emerald-600 hover:bg-emerald-500 rounded-xl gap-2"
                data-testid="save-propuesta-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingPropuesta ? "Actualizar" : "Agregar"} Propuesta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Cotización Modal */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-slate-50 flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-400" /> Compartir Cotización
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Seleccione los usuarios con quienes desea compartir esta cotización
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {platformUsers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No hay otros usuarios disponibles</p>
              ) : (
                platformUsers.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => toggleShareUser(u.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      selectedShareUsers.includes(u.id)
                        ? "bg-violet-500/10 border-violet-500/30"
                        : "bg-slate-800/50 border-slate-800 hover:border-slate-700"
                    }`}
                    data-testid={`share-user-${u.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                      selectedShareUsers.includes(u.id) ? "bg-violet-600 text-white" : "bg-slate-700 text-slate-300"
                    }`}>
                      {(u.name || u.nombre || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate">{u.name || u.nombre || "Sin nombre"}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                    {selectedShareUsers.includes(u.id) && (
                      <Check className="w-5 h-5 text-violet-400 shrink-0" />
                    )}
                  </div>
                ))
              )}
            </div>
            {(cotizacion?.compartida_con?.length > 0) && (
              <div className="border-t border-slate-800 pt-3">
                <p className="text-xs text-slate-500 mb-2">Compartida actualmente con:</p>
                <div className="flex flex-wrap gap-1.5">
                  {cotizacion.compartida_con.map((s, i) => (
                    <Badge key={i} className="bg-slate-800 text-slate-300 text-xs rounded-lg gap-1">
                      <UserPlus className="w-3 h-3" />
                      {s.user_name || s.user_email}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowShareModal(false)} className="border-slate-700 text-slate-300">
                Cancelar
              </Button>
              <Button
                onClick={handleShare}
                disabled={sharing || selectedShareUsers.length === 0}
                className="bg-violet-600 hover:bg-violet-500 rounded-xl gap-2"
                data-testid="share-submit-btn"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Compartir ({selectedShareUsers.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Report Personalization Modal */}
        <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
          <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-slate-50 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" /> Personalizar Informe
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Complete los datos del cliente para generar un informe profesional
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Nombre del Cliente *</Label>
                  <Input
                    value={reportForm.cliente_nombre}
                    onChange={(e) => setReportForm({...reportForm, cliente_nombre: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
                    placeholder="Juan Pérez"
                    data-testid="report-cliente-nombre"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Empresa</Label>
                  <Input
                    value={reportForm.cliente_empresa}
                    onChange={(e) => setReportForm({...reportForm, cliente_empresa: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
                    placeholder="Empresa XYZ SRL"
                    data-testid="report-cliente-empresa"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">RNC</Label>
                  <Input
                    value={reportForm.cliente_rnc}
                    onChange={(e) => setReportForm({...reportForm, cliente_rnc: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
                    placeholder="1-31-12345-6"
                    data-testid="report-cliente-rnc"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Cargo</Label>
                  <Input
                    value={reportForm.cliente_cargo}
                    onChange={(e) => setReportForm({...reportForm, cliente_cargo: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
                    placeholder="Gerente General"
                    data-testid="report-cliente-cargo"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Email</Label>
                  <Input
                    value={reportForm.cliente_email}
                    onChange={(e) => setReportForm({...reportForm, cliente_email: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
                    placeholder="cliente@empresa.com"
                    data-testid="report-cliente-email"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Teléfono</Label>
                  <Input
                    value={reportForm.cliente_telefono}
                    onChange={(e) => setReportForm({...reportForm, cliente_telefono: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
                    placeholder="809-555-1234"
                    data-testid="report-cliente-telefono"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Nota personalizada (opcional)</Label>
                <Textarea
                  value={reportForm.nota_personalizada}
                  onChange={(e) => setReportForm({...reportForm, nota_personalizada: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl min-h-[80px]"
                  placeholder="Agregar comentarios adicionales para el informe..."
                  data-testid="report-nota"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowReportModal(false)} className="border-slate-700 text-slate-300">
                Cancelar
              </Button>
              <Button
                onClick={() => handleExportReport("docx")}
                disabled={exportingReport || !reportForm.cliente_nombre.trim()}
                className="bg-sky-600 hover:bg-sky-500 rounded-xl gap-2"
                data-testid="download-word-btn"
              >
                {exportingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Descargar Word
              </Button>
              <Button
                onClick={() => handleExportReport("pdf")}
                disabled={exportingReport || !reportForm.cliente_nombre.trim()}
                className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white rounded-xl gap-2"
                data-testid="download-pdf-btn"
              >
                {exportingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Descargar PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ==== MODAL: AGREGAR DESDE CATÁLOGO ==== */}
        <Dialog open={showCatalogModal} onOpenChange={setShowCatalogModal}>
          <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-2xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-slate-100 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-400" />
                Agregar Plan desde Catálogo
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Planes disponibles para el departamento de esta cotización
              </DialogDescription>
            </DialogHeader>
            <div className="mb-3">
              <Input
                placeholder="Buscar por nombre de plan o aseguradora..."
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-100"
                data-testid="catalog-search-input"
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 min-h-0 pr-1">
              {catalogPlanes.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No hay planes en el catálogo aún</p>
                  <p className="text-slate-600 text-xs mt-1">Los planes se alimentan automáticamente al analizar cotizaciones</p>
                </div>
              ) : (
                catalogPlanes
                  .filter(p => {
                    if (!catalogSearch) return true;
                    const s = catalogSearch.toLowerCase();
                    return (p.nombre || "").toLowerCase().includes(s) || (p.aseguradora || "").toLowerCase().includes(s);
                  })
                  .map((plan, idx) => {
                    const alreadyAdded = (cotizacion?.propuestas || []).some(
                      pr => pr.plan_nombre?.toLowerCase() === plan.nombre?.toLowerCase() && pr.aseguradora?.toLowerCase() === plan.aseguradora?.toLowerCase()
                    );
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${alreadyAdded ? "border-slate-800 bg-slate-900/50 opacity-60" : "border-slate-800 hover:border-teal-800 bg-slate-900 hover:bg-slate-900/80"}`}
                        data-testid={`catalog-plan-${idx}`}
                      >
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-sm font-medium text-slate-200 truncate">{plan.nombre}</p>
                          <p className="text-xs text-slate-400">{plan.aseguradora}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">{plan.coberturas?.length || 0} coberturas</Badge>
                            {plan.source === "ai_extraction" && (
                              <Badge variant="outline" className="text-[10px] border-sky-800 text-sky-400">IA</Badge>
                            )}
                            {plan.ramo && (
                              <Badge variant="outline" className="text-[10px] border-amber-800 text-amber-400">{plan.ramo}</Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          disabled={alreadyAdded || addingFromCatalog === plan.nombre}
                          onClick={() => handleAddFromCatalog(plan)}
                          className={alreadyAdded ? "bg-slate-800 text-slate-500 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-500 text-white"}
                          data-testid={`catalog-add-${idx}`}
                        >
                          {addingFromCatalog === plan.nombre ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : alreadyAdded ? (
                            <><Check className="w-3.5 h-3.5 mr-1" /> Agregado</>
                          ) : (
                            <><Plus className="w-3.5 h-3.5 mr-1" /> Agregar</>
                          )}
                        </Button>
                      </div>
                    );
                  })
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* === Preview Modal === */}
        <Dialog open={showPreviewModal} onOpenChange={(v) => { if (!v) handleClosePreview(); }}>
          <DialogContent
            className="max-w-[95vw] w-[1240px] h-[90vh] bg-slate-950 border-slate-800 p-0 overflow-hidden flex flex-col"
            data-testid="preview-modal"
          >
            <DialogHeader className="px-5 py-3 border-b border-slate-800 bg-slate-900/60 flex flex-row items-center justify-between space-y-0">
              <div>
                <DialogTitle className="text-slate-100 text-base">Vista Previa de la Propuesta</DialogTitle>
                <DialogDescription className="text-slate-400 text-xs">
                  Así se verá el documento al cliente. Cierra esta ventana y descarga el formato deseado.
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    try {
                      const iframe = document.getElementById("cotizacion-preview-iframe");
                      if (iframe?.contentWindow) iframe.contentWindow.print();
                    } catch {
                      window.open(previewUrl, "_blank");
                    }
                  }}
                  className="border-slate-700 text-slate-200 hover:bg-slate-800 rounded-lg gap-1.5"
                  data-testid="preview-print-btn"
                >
                  <Printer className="w-3.5 h-3.5" /> Imprimir
                </Button>
                <Button
                  size="sm"
                  onClick={() => { handleClosePreview(); handleExportExcel(); }}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg gap-1.5"
                  data-testid="preview-download-excel-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Descargar Excel
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 bg-white overflow-hidden">
              {previewUrl ? (
                <iframe
                  id="cotizacion-preview-iframe"
                  src={previewUrl}
                  title="Vista Previa Cotización"
                  className="w-full h-full border-0"
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
      </div>
    </div>
  );
}
